// ReservationEdit.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    useTheme,
    useMediaQuery,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link,
    Skeleton,
    Stack,
    Grid,
} from '@mui/material';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { IReservation, reservationSchema } from '../Interfaces/IReservation';
import { useUpdateReservation, useCreateReservation, useGetReservationStatus } from '../hooks/reservations';
import { useGetUsers } from '../hooks/users';
import { useGetSystemMail } from '../hooks/mailing';
import SingleAccountField from '../accounts/AccountFieldSingle';
import ReservationInvoice, {InvoiceData} from "./ReservationInvoice";
import {useCreateInvoice, useGetInvoices, useUpdateInvoice} from "../hooks/invoice";
import {useGetReservationPrice} from "../hooks/reservationsPrice";
import {useGetDrinks} from "../hooks/drinks";
import {checkPermission, getCurrentUserId} from "../utils/permission";
import { useAlert } from '../core/AlertContext';
import { sanitizeHtml } from '../utils/sanitizeHtml';

import SectionCard from '../core/SectionCard';
import { GREEN_700, GREEN_900, sectionCardSx, fieldSx, primaryButtonSx } from '../core/pvtaStyles';

interface ReservationEditProps {
    onClose: () => void;
    initialReservation?: IReservation | null;
    showHeading?: boolean;
}

const ReservationEdit: React.FC<ReservationEditProps> = ({ onClose, initialReservation, showHeading = true }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [invoiceOpen, setInvoiceOpen] = useState(false);

    const { createInvoice } = useCreateInvoice();
    const { updateInvoice } = useUpdateInvoice();

    const { userArray, isLoadingUserArray } = useGetUsers();
    const { reservationStatusArray, isLoadingReservationStatusArray } = useGetReservationStatus();
    const { systemMailArray, isLoadingSystemMailArray } = useGetSystemMail();

    const { reservationPriceArray } = useGetReservationPrice();
    const { drinkArray } = useGetDrinks();

    // Abrechnung: Board (Vorstand/Escheck/Superuser) ODER der Buchende selbst
    // (Eigentümer der Reservierung) — der buchende Bundesbruder erstellt seine
    // eigene Abrechnung. Backend erzwingt dieselbe Regel (InvoiceViewSet /
    // IsInvoiceOwnerOrBoard); dieses Gate ist nur die passende UI-Sichtbarkeit.
    const isOwnBooking = !!initialReservation && initialReservation.account === getCurrentUserId();
    const canManageInvoice = checkPermission(2, 3) || isOwnBooking;
    const {invoiceArray} = useGetInvoices(canManageInvoice);

    const [isLoading, setIsLoading] = useState(false)


    // Zustand für Dialog
    const [dialogOpen, setDialogOpen] = useState(false);

    // Hole Hüttenordnung HTML
    const huttenordnungHtml = useMemo(() => {
        return systemMailArray?.find((m) => m.id === 99)?.body ?? '';
    }, [systemMailArray]);

    const defaultReservation = useMemo(
        () => reservationSchema.parse({ booking_status: { id: -1, name: '' } }),
        []
    );
    const [reservation, setReservation] = useState<IReservation>(
        initialReservation ?? defaultReservation
    );

    // State für Checkbox-Bestätigung bei neuer Buchung
    const [confirmChecked, setConfirmChecked] = useState(false);

    useEffect(() => {
        setReservation(initialReservation ?? defaultReservation);
        setConfirmChecked(false);
    }, [initialReservation, defaultReservation]);

    const { updateReservation, isUpdatingReservation } = useUpdateReservation();
    const { createReservation } = useCreateReservation();

    const { showAlert } = useAlert();

    // Escheck-Buchungen max. 12 Monate im Voraus; Admins/Escheck-Team (Gruppe 2/3) ausgenommen.
    const canBypassLeadLimit = checkPermission(2, 3);
    const maxBookingDate = useMemo(() => {
        const limit = new Date();
        limit.setDate(limit.getDate() + 365);
        return limit.toISOString().split('T')[0];
    }, []);




    const existingInvoice = useMemo(() => {
        if (!reservation?.id || !invoiceArray) return null;
        return invoiceArray.find(inv => inv.reservation_id === reservation.id) || null;
    }, [reservation?.id, invoiceArray]);


    const handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | React.ChangeEvent<{ name?: string; value: unknown }>
    ) => {
        const { name, value } = e.target;
        setReservation((prev) => ({
            ...prev,
            [name as string]:
                name === 'booking_status_id' && typeof value === 'string'
                    ? parseInt(value, 10)
                    : value,
        }));
    };

    const handleSelectChange = (event: SelectChangeEvent<number>) => {
        const { name, value } = event.target;
        if (name === 'booking_status_id') {
            const id = typeof value === 'string' ? parseInt(value, 10) : value;
            const status = reservationStatusArray.find((s) => s.id === id);
            setReservation((prev) => ({ ...prev, booking_status: status ?? prev.booking_status }));
        } else {
            setReservation((prev) => ({ ...prev, [name]: typeof value === 'string' ? parseInt(value, 10) : value }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!initialReservation && !confirmChecked) return;
        try {
            const payload: any = { ...reservation };
            if (reservation.booking_status) payload.booking_status_id = reservation.booking_status.id;
            if (reservation.id === -1) {
                delete payload.booking_status;
                payload.booking_status_id = 1;
                await createReservation(payload);
            } else {
                await updateReservation(payload);
            }
            showAlert({ text: 'Buchung erfolgreich gespeichert.' });
            onClose();
        } catch (error) {
            console.error('Fehler beim Speichern:', error);
            showAlert({ text: 'Fehler beim Speichern der Buchung.', severity: 'error' });
        }
    };



    const handleInvoiceSubmit = async (invoiceData: InvoiceData) => {

        setIsLoading(true)

        const existingInvoice = invoiceArray.find(inv => inv.reservation_id === reservation.id);

        const payload = {
            reservation_id: reservation.id,
            cleaning_fee_id: invoiceData.cleaningFeeId,
            overnight_items: Object.entries(invoiceData.counts)
                .map(([price_item_id, quantity]) => {
                    const id = Number(price_item_id);
                    const priceItem = reservationPriceArray.find(p => p.id === id);
                    return {
                        price_item_id: id,
                        quantity,
                        unit_price: priceItem ? Number(priceItem.price) : 0,
                    };
                })
                .filter(item => item.quantity > 0),
            drink_items: Object.entries(invoiceData.drinkCounts)
                .map(([drink_id, quantity]) => {
                    const id = Number(drink_id);
                    const drink = drinkArray.find(d => d.id === id);
                    return {
                        drink_id: id,
                        quantity,
                        unit_price: drink ? Number(drink.price) : 0,
                    };
                })
                .filter(item => item.quantity > 0),
        };

        try {
            if (existingInvoice) {
                await updateInvoice({
                    id: existingInvoice.id,
                    ...payload,
                    total: invoiceData.total,
                });
            } else {
                await createInvoice(payload);
            }

            const abgerechnetStatus = reservationStatusArray.find(s => s.id === 4);
            if (abgerechnetStatus) {
                setReservation(prev => ({
                    ...prev,
                    booking_status: abgerechnetStatus
                }));
            }
            setInvoiceOpen(false);
            setIsLoading(false)
        } catch (err) {
            console.error('Fehler beim Speichern der Rechnung', err);
            setIsLoading(false)
        }
    };



    if (isLoadingUserArray || isLoadingReservationStatusArray || isLoadingSystemMailArray || isLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
                <Skeleton variant="text" width="45%" height={34} />
                {initialReservation && <Skeleton variant="rounded" width={220} height={36} />}
                <Skeleton variant="rounded" height={88} />
                <Skeleton variant="text" width={150} />
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                    {[0, 1, 2].map((item) => (
                        <Skeleton key={item} variant="rounded" height={40} sx={{ flex: 1 }} />
                    ))}
                </Box>
                <Skeleton variant="text" width={100} />
                <Skeleton variant="rounded" height={88} />
                {!initialReservation && <Skeleton variant="text" width="75%" />}
                {initialReservation && (
                    <>
                        <Skeleton variant="text" width={180} />
                        <Stack spacing={2}>
                            <Skeleton variant="rounded" height={40} />
                            <Skeleton variant="rounded" height={40} />
                        </Stack>
                    </>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                    <Skeleton variant="rounded" width={96} height={36} />
                    <Skeleton variant="rounded" width={150} height={36} />
                </Box>
            </Box>
        );
    }

    return (
        <>

            <ReservationInvoice
                reservation={reservation}
                open={invoiceOpen}
                onClose={() => setInvoiceOpen(false)}
                onSubmit={handleInvoiceSubmit}
                initialInvoice={existingInvoice} // <--- NEU
            />


        <Box
            component="form"
            id="reservation-edit-form"
            onSubmit={handleSubmit}
            sx={{ maxWidth: '100%', width: '100%' }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 2, sm: 3 } }}>
                {showHeading && (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: GREEN_900 }}>
                        {initialReservation ? 'Echeck-Buchung bearbeiten' : 'Neue Escheck-Buchung anlegen'}
                    </Typography>
                )}

                {canManageInvoice && initialReservation && new Date(initialReservation.end_date) <= new Date() && (
                    <Button
                        variant="outlined"
                        onClick={() => setInvoiceOpen(true)}
                        startIcon={<ReceiptLongOutlinedIcon />}
                        sx={{
                            alignSelf: 'flex-start',
                            borderRadius: 999,
                            fontWeight: 600,
                            color: GREEN_900,
                            borderColor: 'var(--pvta-border)',
                            '&:hover': { borderColor: GREEN_700, backgroundColor: 'var(--pvta-green-100)' },
                        }}
                    >
                        {existingInvoice ? 'Abrechnung anzeigen / bearbeiten' : 'Abrechnung starten'}
                    </Button>
                )}

                {/* Buchungsgrund */}
                <SectionCard title="Buchungsgrund" icon={<EditNoteRoundedIcon fontSize="small" />}>
                    <TextField
                        label="Buchungsgrund"
                        name="booking_reason"
                        value={reservation.booking_reason}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                        sx={fieldSx}
                        required
                        // disabled, wenn der Status >1 UND NICHT isEscheckTeam ist
                        disabled={
                            !checkPermission(3) &&
                            // safe access: falls booking_status undefined ist, nehmen wir ID = 0
                            (reservation.booking_status?.id ?? 0) > 1
                        }
                    />
                </SectionCard>

                {/* Buchungsdaten */}
                <SectionCard title="Buchungsdaten" icon={<CalendarMonthRoundedIcon fontSize="small" />}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Startdatum"
                                type="date"
                                name="start_date"
                                value={reservation.start_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                                sx={fieldSx}
                                inputProps={canBypassLeadLimit ? undefined : { max: maxBookingDate }}
                                helperText={canBypassLeadLimit ? undefined : 'Max. 12 Monate im Voraus.'}
                                disabled={
                                    !checkPermission(3) &&
                                    (reservation.booking_status?.id ?? 0) > 1
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Enddatum"
                                type="date"
                                name="end_date"
                                value={reservation.end_date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                                sx={fieldSx}
                                disabled={
                                    !checkPermission(3) &&
                                    (reservation.booking_status?.id ?? 0) > 1
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Anzahl Zimmer"
                                type="number"
                                name="amount_rooms"
                                value={reservation.amount_rooms}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                required
                                fullWidth
                                sx={fieldSx}
                                disabled={
                                    !checkPermission(3) &&
                                    (reservation.booking_status?.id ?? 0) > 1
                                }
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                    </Grid>
                </SectionCard>

                {/* Notizen */}
                <SectionCard title="Notizen" icon={<StickyNote2OutlinedIcon fontSize="small" />}>
                    <TextField
                        label="Notizen"
                        name="notes"
                        value={reservation.notes || ''}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                        sx={fieldSx}
                    />
                </SectionCard>

                {/* Bestätigungs-Checkbox nur bei neuer Buchung */}
                {!initialReservation && (
                    <Box sx={{ ...sectionCardSx, p: { xs: 2, md: 2.5 } }}>
                        <FormControl required>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={confirmChecked}
                                        onChange={(e) => setConfirmChecked(e.target.checked)}
                                        name="confirmation"
                                        required
                                        sx={{ color: GREEN_700, '&.Mui-checked': { color: GREEN_700 } }}
                                    />
                                }
                                label={
                                    <>
                                        Ich bestätige, dass die vorstehenden Angaben zu meiner Buchung richtig sind und dass ich mit der{' '}
                                        <Link component="button" type="button" onClick={() => setDialogOpen(true)}>
                                            Hüttenordnung
                                        </Link>{' '}
                                        einverstanden bin
                                    </>
                                }
                            />
                        </FormControl>
                    </Box>
                )}

                {initialReservation && (
                    <SectionCard title="Status und Zuordnung" icon={<AssignmentIndOutlinedIcon fontSize="small" />}>
                        <Stack spacing={2}>
                            <SingleAccountField
                                allAccounts={userArray!}
                                selectedAccount={userArray!.find((u) => u.id === reservation.account) || null}
                                onChange={(newAccount) => setReservation((prev) => ({ ...prev, account: newAccount?.id ?? prev.account }))}
                                label="Bundesbruder"
                                disabled={!checkPermission(3)}
                            />
                            {isLoadingReservationStatusArray ? (
                                <Skeleton variant="rounded" height={40} />
                            ) : (
                                <FormControl fullWidth required sx={fieldSx}>
                                    <InputLabel id="booking-status-label">Buchungsstatus</InputLabel>
                                    <Select
                                        labelId="booking-status-label"
                                        label="Buchungsstatus"
                                        name="booking_status_id"
                                        value={reservation.booking_status?.id ?? ''}
                                        onChange={handleSelectChange}
                                        disabled={!checkPermission(3)}
                                    >
                                        {reservationStatusArray.sort((a, b) => a.id - b.id).map((status) => (
                                            <MenuItem key={status.id} value={status.id}>
                                                {status.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>
                    </SectionCard>
                )}
            </Box>

            {/* Sticky Footer */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    pt: 1.5,
                    pb: `calc(env(safe-area-inset-bottom) + 12px)`,
                    backgroundColor: 'rgba(248, 245, 239, 0.92)',
                    backdropFilter: 'blur(6px)',
                    borderTop: '1px solid var(--pvta-border)',
                    boxShadow: '0 -8px 24px rgba(20, 35, 25, 0.06)',
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        minHeight: 44,
                        borderRadius: 999,
                        fontWeight: 600,
                        px: 2.5,
                        color: GREEN_900,
                        borderColor: 'var(--pvta-border)',
                        '&:hover': { borderColor: GREEN_700, backgroundColor: 'var(--pvta-green-100)' },
                    }}
                >
                    Abbrechen
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    disabled={isUpdatingReservation || (!initialReservation && !confirmChecked)}
                    sx={{ ...primaryButtonSx }}
                >
                    Buchung speichern
                </Button>
            </Box>
        </Box>

            {/* Dialog für Hüttenordnung */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0px 20px 45px rgba(15, 23, 42, 0.16)',
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Hüttenordnung</DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    {/* Render HTML direkt */}
                    <Box component="div" dangerouslySetInnerHTML={{ __html: sanitizeHtml(huttenordnungHtml) }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} variant="text">Schließen</Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default ReservationEdit;
